/**
 * The week strip, without a page load.
 *
 * Every day of the week is already on the page: the server read the whole week
 * in one go and rendered a panel for each of them. So a tap on a lamp is not a
 * navigation, it is a choice of which panel to show - no request, no reload, no
 * flash of a page rebuilding itself around the same seven lamps.
 *
 * The URL still follows, and every lamp is still a real link. That is what
 * keeps the two ways of arriving at a day the same one: a cold load of
 * /daily/results?date=X renders X, and so does a tap that replaced it. Nothing
 * here decides what a day looks like - the server already did.
 *
 * A thumb sweeping across the page is the same choice made without aiming: the
 * days are in order, so left and right are the days either side of this one.
 */

/**
 * Which day to show, given the date asked for and the days on the page.
 *
 * opened is the day the server chose, which is what /daily/results with no date
 * renders - so it is where a URL with no date lands, and where going back to
 * one has to return to. A date with no panel is a URL somebody typed, and is
 * worth the same answer.
 */
export function dayToShow({ wanted, days, opened }) {
    if (days.includes(wanted)) return wanted;
    if (days.includes(opened)) return opened;
    return null;
}

/**
 * Whether a click on a lamp is ours to answer.
 *
 * Anything but a plain left click is the reader asking for a page rather than a
 * panel - a new tab, a new window, a saved link. The href is real, so those are
 * left to the browser.
 */
export function takesOver(event) {
    if (!event || event.defaultPrevented) return false;
    if (typeof event.button === 'number' && event.button !== 0) return false;
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * Which way a drag across the page means, or 0 where it means nothing.
 *
 * A finger moving left pulls the next day in from the right, which is the way
 * a stack of cards moves under a thumb. Anything shorter than the threshold is
 * a tap that wandered, and anything steeper than it is wide is the page being
 * scrolled: a panel is a column of text, and reading it must not change the
 * day underneath.
 */
export function swipeStep({ dx, dy, threshold = 48 }) {
    if (!(Math.abs(dx) >= threshold)) return 0;
    if (Math.abs(dx) <= Math.abs(dy)) return 0;
    return dx < 0 ? 1 : -1;
}

/**
 * The day one step along from another, or null at the two ends of the week.
 *
 * The week does not wrap. Sunday's next day is not Monday's page - it is a day
 * this page is not holding - and a swipe that spun the strip back to its start
 * would lose the one thing the order is for.
 */
export function neighbourDay({ days, from, step }) {
    const at = days.indexOf(from);
    if (at < 0) return null;
    const next = at + step;
    if (next < 0 || next >= days.length) return null;
    return days[next];
}

/**
 * Wires the strip up. Returns a teardown, or null where there is no week to
 * steer - which covers every other page, and a page rendered before this
 * existed, whose lamps must keep working as links.
 */
export function createWeekStrip(root = document) {
    const strip = root.querySelector('[data-week]');
    if (!strip) return null;

    const panels = new Map();
    for (const panel of root.querySelectorAll('[data-day-panel]')) {
        panels.set(panel.dataset.dayPanel, panel);
    }
    if (panels.size < 2) return null;

    const lamps = new Map();
    for (const lamp of strip.querySelectorAll('[data-week-day]')) {
        lamps.set(lamp.dataset.weekDay, lamp);
    }

    /** The day showing right now. */
    const current = () => {
        for (const [date, panel] of panels) if (!panel.hidden) return date;
        return null;
    };

    const opened = current();
    const days = [...panels.keys()];
    const header = root.querySelector('[data-day-date]');
    // The title's own suffix, kept rather than rebuilt: the day's name is the
    // only part of it that changes.
    const suffix = document.title.slice(document.title.indexOf(' ·'));

    function show(wanted) {
        const date = dayToShow({ wanted, days, opened });
        if (date === null) return;

        for (const [d, panel] of panels) panel.hidden = d !== date;

        for (const [d, lamp] of lamps) {
            if (d === date) {
                lamp.setAttribute('aria-current', 'page');
            } else {
                lamp.removeAttribute('aria-current');
            }
            // The lamp's own pulse. Written out in the template too, so it is a
            // class the stylesheet has seen.
            lamp.firstElementChild?.classList.toggle('peg--active', d === date);
        }

        const panel = panels.get(date);
        if (header) header.textContent = panel.dataset.dayLabel || '';
        document.title = (panel.dataset.dayNameText || '') + suffix;
    }

    /**
     * A swap the browser can animate, where it offers to. Without it the panels
     * simply change over, which is what this had to do anyway.
     */
    function swap(wanted) {
        if (typeof document.startViewTransition !== 'function') {
            show(wanted);
            return;
        }
        document.startViewTransition(() => show(wanted));
    }

    /**
     * Goes to a day, by the one route every way in shares: the URL the lamp
     * already carries. A day reached by a tap, a swipe or a paste is the same
     * day.
     *
     * The URL is replaced rather than pushed. The week is one page, and back
     * from a page goes up out of it - not through every day it was turned to.
     */
    function go(date) {
        if (!panels.has(date)) return;
        if (date !== current()) {
            const href = lamps.get(date)?.getAttribute('href');
            window.history.replaceState({ ...window.history.state, date }, '', href || `?date=${date}`);
        }
        swap(date);
    }

    function onClick(event) {
        const lamp = event.target.closest?.('[data-week-day]');
        if (!lamp || !strip.contains(lamp) || !takesOver(event)) return;

        const wanted = lamp.dataset.weekDay;
        if (!panels.has(wanted)) return;

        event.preventDefault();
        go(wanted);
    }

    // Where a drag started, and whether it is still one finger. A second finger
    // is a pinch or a two-handed scroll, and neither of them is asking for
    // another day.
    let drag = null;

    function onTouchStart(event) {
        if (event.touches.length !== 1) {
            drag = null;
            return;
        }
        const touch = event.touches[0];
        drag = { x: touch.clientX, y: touch.clientY };
    }

    function onTouchMove(event) {
        if (drag && event.touches.length !== 1) drag = null;
    }

    function onTouchEnd(event) {
        const start = drag;
        drag = null;
        if (!start || event.changedTouches.length !== 1) return;

        const touch = event.changedTouches[0];
        const step = swipeStep({ dx: touch.clientX - start.x, dy: touch.clientY - start.y });
        if (step === 0) return;

        const wanted = neighbourDay({ days, from: current(), step });
        if (wanted === null) return;

        // A finger that ends on a link still produces a click, and half the
        // page is links - the invite button posts. Cancelling the touch is what
        // stops the swipe from also pressing whatever it finished over.
        if (event.cancelable) event.preventDefault();
        go(wanted);
    }

    function onTouchCancel() {
        drag = null;
    }

    // The whole page rather than the strip: the days are read below it, and a
    // thumb sweeping across what it is reading is where the gesture belongs.
    const page = root.querySelector('[data-week-page]') || strip;

    strip.addEventListener('click', onClick);
    // Down and move stay passive: the day changes on the way up, so neither of
    // them ever cancels a scroll. Up is not, because a swipe has to be able to
    // take the click that would otherwise follow it.
    page.addEventListener('touchstart', onTouchStart, { passive: true });
    page.addEventListener('touchmove', onTouchMove, { passive: true });
    page.addEventListener('touchend', onTouchEnd);
    page.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
        strip.removeEventListener('click', onClick);
        page.removeEventListener('touchstart', onTouchStart);
        page.removeEventListener('touchmove', onTouchMove);
        page.removeEventListener('touchend', onTouchEnd);
        page.removeEventListener('touchcancel', onTouchCancel);
    };
}
